import { openPortalRemoteSession,endPortalRemoteSession,portalRemoteSocketUrl } from '../lib/api';
import { type ClipboardCommand, type ConsentOutcome, RECONNECT_SECONDS, type SessionEndReason, type ViewerCommand, type ViewerMessage } from '@api/remote/contracts';
import { ALL_DISPLAYS, ctrlAltDelFrame, displaysFrame, inputLockFrame, jpegSize, keyFrame, type Monitor, mouseFrame, MouseButton, pauseFrame,
  refreshFrame, ScreenDecoder, type ScreenEvent, setDisplayFrame, settingsFrame, unicodeFrame, wheelFrame } from '@api/remote/desktop-protocol';
import { approveRemoteSession, endRemoteSession, getRemoteStatus, isStatus, listApprovalKeys, openRemoteSession, remoteSessionSocketUrl,
  restartRemoteDevice } from '../lib/api';
import { describeKeyError, signWithKey, webauthnAvailable } from '../lib/webauthn';
import { isCommand, isModifier, SPECIAL_KEYS, typesCharacter, type VirtualKey, virtualKey } from './keys';

export type Quality = 'fast' | 'balanced' | 'sharp';
/** JPEG quality, scaling (1024ths) and minimum milliseconds between frames for each choice in the toolbar. */
export const QUALITY: Record<Quality, { label: string; detail: string; quality: number; scaling: number; frameMs: number }> = {
  fast: { label: 'Fastest', detail: 'For slow connections: softer picture, smaller screen', quality: 35, scaling: 768, frameMs: 50 },
  balanced: { label: 'Balanced', detail: 'Good picture and responsive', quality: 55, scaling: 1024, frameMs: 50 },
  sharp: { label: 'Sharpest', detail: 'Best for reading small text', quality: 85, scaling: 1024, frameMs: 80 },
};

/** What the viewer says once the person at the computer (or the customer's setting) let the technician in. */
const CONSENT_NOTICE: Partial<Record<ConsentOutcome, string>> = {
  allowed: 'The person at the computer allowed this session.',
  timeout_allowed: 'Nobody answered in time, and this customer lets you in when that happens.',
  unattended_allowed: 'Nobody was at the computer to answer, and this customer lets you in when that happens.',
};

/**
 * How often the viewer looks whether a device that went offline is back (WRMM-66); after a restart it waits a little
 * first, as the device may still be shutting down.
 */
const RECONNECT_POLL_MS = 3_000, RESTART_GRACE_MS = 10_000;
/**
 * Ends that a reconnect may continue: the device went offline; for a view-only session, which needs no key touch, also
 * WITTCO RMM restarting. Taking control again after a WITTCO RMM restart needs the key, as usual.
 */
const RECONNECTABLE = { control: new Set<SessionEndReason>(['lost']), view: new Set<SessionEndReason>(['lost', 'interrupted']) };
/** Ends of a reconnect attempt after which the viewer stops trying: the technician or the person at the computer said no. */
const FINAL = new Set<SessionEndReason>(['closed', 'revoked', 'removed', 'declined', 'unanswered', 'expired']);

/**
 * How long the viewer waits for a copy or paste to be answered: the relay's whole bounded path, up to three audit
 * writes (each may wait 5 seconds for a database connection and 5 to run) around MeshAgent's 15 seconds.
 */
const CLIPBOARD_WAIT_MS = 60_000;
const WAITING_NOTICE = 'Waiting for the device to show its screen.';
/** The longest a display switch waits for the device to show the display asked for. */
const SWITCH_MS = 10_000;
/** Tiles decoded at once, and the most tile data held waiting to be drawn before the backlog is dropped. */
const DECODE_AHEAD = 4, MAX_QUEUED_TILE_BYTES = 32 * 1024 * 1024;
/** The largest screen the viewer draws: 16,384 pixels a side and 64 million in all. */
export const MAX_SCREEN_SIDE = 16_384, MAX_SCREEN_PIXELS = 64 * 1024 * 1024;
/**
 * MeshAgent cuts the screen into tiles padded to whole tiles (32 pixels, measured: a 1366x768 screen's first full
 * picture is 1376x768), so a picture at the right or bottom edge can run past the screen's size. Up to the next 64 is
 * drawn, clipped by the canvas; anything larger isn't a screen update.
 */
const TILE_PADDING = 64;
const padded = (side: number) => Math.ceil(side / TILE_PADDING) * TILE_PADDING;

export interface ViewerState {
  desktopBackend: 'native' | 'wayland';
  /**
   * approving: a control session waits for the technician's security key before it connects. reconnecting: the device
   * went offline (restarting, say), and the viewer reconnects by itself when it is back (WRMM-66).
   */
  phase: 'starting' | 'approving' | 'connecting' | 'connected' | 'reconnecting' | 'ended';
  /** While approving: whether the key is being asked, and what went wrong last time. */
  approval: { busy: boolean; error: string | null } | null;
  sessionId: string | null;
  /** A message from the device, such as that it's waiting for its user to allow the session. */
  notice: string | null;
  /**
   * The customer's consent prompt (WRMM-64): waiting for the person at the computer (or, with nobody there to answer,
   * for the time to run out), from `since`; then how it went.
   */
  consent: { state: 'waiting'; seconds: number; unattended: boolean; letInAfter: boolean; since: number } | { state: ConsentOutcome } | null;
  ended: { reason: SessionEndReason | 'refused'; message: string } | null;
  screen: { width: number; height: number } | null;
  /** The device's displays (with ALL_DISPLAYS when it has several), the one shown, and where each monitor is (WRMM-67). */
  displays: number[]; display: number | null; monitors: Monitor[];
  /** Switching to another display: input waits for its picture, so a click can't land on the one being left. */
  switching: boolean;
  /** Whether the device's own keyboard and mouse are blocked; null until it says. */
  inputLocked: boolean | null;
  cursor: number;
  connectedAt: number | null;
  /** Screen data received per second, smoothed. */
  rate: number;
  /** The technician restarted the device from this viewer (WRMM-66); it goes offline shortly. */
  restart: { safeMode: boolean } | null;
  /**
   * While reconnecting: since when the device has been gone and until when this viewer may reconnect without a key
   * touch; whether it was restarted from here; and whether the relay is still finding out if the device went offline.
   */
  reconnect: { since: number; until: number; restart: boolean; checking: boolean } | null;
}
export const initialViewer: ViewerState = { desktopBackend: 'native', phase: 'starting', approval: null, sessionId: null, notice: null, consent: null, ended: null, screen: null, displays: [],
  display: null, monitors: [], switching: false, inputLocked: null, cursor: 0, connectedAt: null, rate: 0, restart: null, reconnect: null };

/**
 * One remote desktop session in the browser: asks the API for a session, connects to its relay and keeps the canvas in
 * step with the device's screen. Input goes out as desktop commands; the API passes on only what the session may send.
 */
export class RemoteViewer {
  private socket: WebSocket | null = null;
  private decoder = new ScreenDecoder();
  /** Whether the session being relayed now was let in; only such a session can be reconnected to. */
  private letIn = false;
  /** The dropped session the viewer is reconnecting to (its key touch covers the reconnect), and until when. */
  private continuing: { sessionId: string; until: number } | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private switchTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * Input held until the picture shown is the screen input lands on (WRMM-67). 'confirm': this viewer asked for another
   * display and waits for the device's display list to name it (MeshAgent sends it after the new size); nothing about
   * another display, such as a late answer to an earlier switch, completes it. 'picture': the display is known and input
   * waits for its first picture to be drawn: after this viewer's switch, another viewer's (MeshAgent shows one screen
   * to every session on a device), or a new screen size. 'resync': that didn't happen in time (or the switch wasn't
   * passed on), so the viewer has asked the device again which display it shows and for its picture, and goes by
   * whatever it says; input stays held meanwhile, however long it takes.
   */
  private switchStage: 'confirm' | 'resync' | 'picture' | null = null;
  private switchTarget: number | null = null;
  /** The display the device last said it shows. */
  private shownDisplay: number | null = null;
  /** A notice display switching put up (waiting, or a switch refused): cleared once a switch completes. */
  private switchNotice: string | null = null;
  private state: ViewerState = initialViewer;
  private quality: Quality = 'balanced';
  /** Tiles are decoded in parallel but drawn in the order they came, so a later tile is never painted over. */
  private tiles: Array<{ blob: Blob; x: number; y: number; ready: Promise<ImageBitmap | null> | null }> = [];
  /** Bytes of tiles waiting to be drawn, and decodes still running (including those of dropped tiles). */
  private queued = 0;
  private decoding = 0;
  private lastRefresh = 0;
  private drawing = false;
  private received = 0;
  private rateTimer: ReturnType<typeof setInterval> | null = null;
  private pointer: { x: number; y: number } | null = null;
  private pointerFrame: number | null = null;
  private wheel = 0;
  private closed = false;
  private approvalOptions: Record<string, unknown> | null = null;
  /** Characters sent as down for each physical key, so the up matches even if Shift was let go first. */
  private typed = new Map<string, number[]>();
  private held = new Map<string, { code: number; extended: boolean }>();
  /** Keys pressed with ⌘ on a Mac, already let go on the device: macOS never reports their release. */
  private released = new Set<string>();
  private keyboard = { mac: false, commandAsCtrl: false };
  /** Mouse buttons sent as down, and where. */
  private buttons = new Map<number, { x: number; y: number }>();
  /** Copy and paste requests waiting for the relay's answer (WRMM-63), by their ID. */
  private clips = new Map<string, { resolve: (answer: { bytes: number; text?: string }) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  private clipCount = 0;

  constructor(private readonly options: {
    mspId: string; deviceId: string; mode: 'control' | 'view'; customer?: boolean; canvas: HTMLCanvasElement; onChange: (state: ViewerState) => void;
  }) {}

  private set(patch: Partial<ViewerState>) { this.state = { ...this.state, ...patch }; this.options.onChange(this.state); }
  private send(frame: Uint8Array) { if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(new Uint8Array(frame)); }
  get control() { return this.options.mode === 'control'; }

  async start() {
    let ticket;
    try { ticket = await (this.options.customer ? openPortalRemoteSession(this.options.deviceId) : openRemoteSession(this.options.mspId, this.options.deviceId, this.options.mode)); } catch (error) {
      const status = (error as { status?: number }).status;
      // Taking control needs an approval key; without one, say that rather than blaming the device.
      const keyless = !this.options.customer && status === 409 && this.control && await listApprovalKeys().then(keys => !keys.length, () => false);
      this.set({ phase: 'ended', ended: { reason: 'refused', message: keyless
        ? 'Taking control of a device needs your security key or passkey, and you haven’t registered one. Add one in Settings, or connect view only.'
        : status === 403 ? 'You don’t have remote access to this device.'
        : status === 409 ? 'The device isn’t connected to WITTCO Remote right now. Check that it’s on and online, then try again.'
        : status === 404 ? 'This device isn’t available to you.' : 'The session couldn’t be started. Try again in a moment.' } });
      return;
    }
    if (this.closed) { this.abandon(ticket.id); return; } // The window closed while the session was being asked for.
    if (ticket.approval) {
      this.approvalOptions = ticket.approval;
      this.set({ phase: 'approving', sessionId: ticket.id, approval: { busy: false, error: webauthnAvailable() ? null
        : 'Security keys need a secure address: open the console at localhost or over HTTPS.' } });
      return;
    }
    this.connect(ticket.id);
  }

  /** Asks the technician's key to approve this control session, then connects. Called from a click (browsers require one). */
  async approve() {
    const sessionId = this.state.sessionId, options = this.approvalOptions;
    if (this.state.phase !== 'approving' || !sessionId || !options || this.state.approval?.busy) return;
    this.set({ approval: { busy: true, error: null } });
    try {
      await approveRemoteSession(this.options.mspId, sessionId, await signWithKey(options));
    } catch (error) {
      if (this.closed) return;
      // The challenge is used once: a refused or expired approval needs a new session.
      const expired = isStatus(error, 404) || isStatus(error, 403);
      if (expired) {
        this.abandon(sessionId); // The server may still hold it open; close it.
        this.set({ phase: 'ended', ended: { reason: 'refused', message: 'The approval wasn’t accepted or took too long. Reconnect to try again.' } });
        return;
      }
      this.set({ approval: { busy: false, error: (error as { status?: number })?.status ? 'The server didn’t accept the approval.' : describeKeyError(error) } });
      return;
    }
    if (!this.closed) this.connect(sessionId);
  }

  private connect(sessionId: string) {
    // A reconnect starts without the dropped session's screen, displays and input lock: the device may come back with
    // others, and no input is mapped to it until it says what its screen is now.
    const fresh = this.continuing ? { screen: null, displays: [], display: null, monitors: [], switching: false, inputLocked: null, cursor: 0 } : {};
    this.switchStage = null;
    this.switchTarget = null;
    this.shownDisplay = null;
    this.set({ phase: this.continuing ? 'reconnecting' : 'connecting', sessionId, approval: null, desktopBackend: 'native', ...fresh });
    // Each session starts afresh: its own screen stream, timers and state.
    this.decoder = new ScreenDecoder();
    this.over = false;
    this.letIn = false;
    const socket = new WebSocket(this.options.customer ? portalRemoteSocketUrl(sessionId) : remoteSessionSocketUrl(this.options.mspId, sessionId));
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    socket.onmessage = event => {
      if (this.socket !== socket) return; // A session already left behind (its device dropped it).
      if (typeof event.data === 'string') { this.onMessage(event.data); return; }
      this.received += event.data.byteLength;
      let screens: ScreenEvent[];
      try { screens = this.decoder.push(new Uint8Array(event.data)); } catch (error) {
        this.fail(`${(error as Error).message}, so the session was ended.`);
        return;
      }
      for (const screen of screens) this.onScreen(screen);
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.stopTimers();
      if (this.state.phase === 'ended') return;
      // Refused or lost before the relay said it ended: make sure the session is closed.
      this.abandon(sessionId);
      // A reconnect the relay turned away (the device not quite back, say) is tried again while there is time.
      if (this.continuing && !this.letIn && !this.closed) { this.waitForDevice(RECONNECT_POLL_MS); return; }
      this.set({ phase: 'ended', reconnect: null, ended: { reason: 'failed', message: 'The connection to the session was lost.' } });
    };
    this.rateTimer = setInterval(() => { this.set({ rate: Math.round(this.state.rate * 0.5 + this.received * 0.5) }); this.received = 0; }, 1000);
  }

  /** Ends the session; the device's user sees it close. `failed`: the device sent something this viewer can't show. */
  end(reason?: 'failed') {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.continuing = null;
    // Not connected to the device yet (asking, approving, or the viewer's connection still opening): close the request
    // through the API too, rather than leave it open. Once connected, the relay ends it when this socket closes.
    if (this.state.sessionId && this.state.phase !== 'connected') this.abandon(this.state.sessionId);
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ type: 'end', ...(reason ? { reason } : {}) } satisfies ViewerCommand));
    if (this.state.phase !== 'ended') this.set({ phase: 'ended', reconnect: null, ended: { reason: 'closed', message: 'You ended the session.' } });
    this.socket?.close();
    this.stopTimers();
  }

  /** Ends a session the device broke (something too large or malformed to show); recorded as failed. */
  private fail(message: string) {
    this.end('failed');
    this.set({ ended: { reason: 'failed', message } });
  }

  private readonly abandoned = new Set<string>();
  private abandon(sessionId: string) {
    if (this.abandoned.has(sessionId)) return;
    this.abandoned.add(sessionId);
    void (this.options.customer ? endPortalRemoteSession(sessionId) : endRemoteSession(this.options.mspId, sessionId)).catch(() => undefined);
  }

  /**
   * Restarts the device (WRMM-66), normally or into Safe Mode with networking: covered by this session's key touch. The
   * session drops as the device goes offline, and the viewer reconnects when it is back.
   */
  async restart(safeMode: boolean) {
    const sessionId = this.state.sessionId;
    if (!this.control || this.state.phase !== 'connected' || !sessionId || this.state.restart) return;
    // Pending from the moment it is asked, so it can't be asked twice (and the device may go down before the answer).
    this.set({ restart: { safeMode }, notice: safeMode ? 'Restarting in Safe Mode with networking… You’ll be reconnected when it’s back.'
      : 'Restarting… You’ll be reconnected when it’s back.' });
    try {
      await restartRemoteDevice(this.options.mspId, sessionId, safeMode);
    } catch (error) {
      if (this.state.sessionId !== sessionId || this.state.phase !== 'connected') return; // The session moved on meanwhile.
      // Its answer may have been lost after it went out: say to wait, so a second restart doesn't follow the first.
      const notice = isStatus(error, 503) ? 'WITTCO Remote couldn’t confirm the device got the restart, so it may still restart. Wait a minute before trying again.'
        : 'The device couldn’t be restarted from here.';
      // Safe Mode is set before the restart is sent, so it may be set although the restart didn't go out.
      this.set({ restart: null,
        notice: safeMode ? `${notice} It may still start in Safe Mode the next time it restarts: restart it normally from here to undo that.` : notice });
    }
  }

  /**
   * The device went offline (or WITTCO RMM restarted) during a session that was let in: the viewer waits for the device to
   * come back and reconnects without a key touch, for RECONNECT_SECONDS from the drop (WRMM-66). Each reconnect is a new
   * session continuing the one that dropped; the customer's consent setting applies to it as to any other.
   */
  private reconnectAfter(sessionId: string) {
    // From when the device's side closed (the relay may have taken a while to confirm it went offline).
    const since = this.state.reconnect?.since ?? Date.now(), restart = !!this.state.restart || !!this.state.reconnect?.restart;
    this.continuing = { sessionId, until: since + RECONNECT_SECONDS * 1000 };
    this.set({ phase: 'reconnecting', restart: null, consent: null, notice: null, connectedAt: null,
      reconnect: { since, until: this.continuing.until, restart, checking: false } });
    this.waitForDevice(restart ? RESTART_GRACE_MS : RECONNECT_POLL_MS);
  }

  private waitForDevice(delay: number) {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => void this.tryReconnect(), delay);
  }

  private async tryReconnect() {
    const continuing = this.continuing;
    if (!continuing || this.closed) return;
    if (Date.now() > continuing.until) {
      this.continuing = null;
      this.set({ phase: 'ended', reconnect: null, ended: { reason: 'lost',
        message: `The device didn’t come back within ${RECONNECT_SECONDS / 60} minutes. Reconnect to start a new session.` } });
      return;
    }
    let ticket;
    try {
      const status = await getRemoteStatus(this.options.mspId, this.options.deviceId);
      if (status.state !== 'ready') { this.waitForDevice(RECONNECT_POLL_MS); return; }
      ticket = await openRemoteSession(this.options.mspId, this.options.deviceId, this.options.mode, continuing.sessionId);
    } catch (error) {
      if (this.closed) return;
      // Refused outright (too late for this session, or access changed): starting again needs a key touch. Anything else
      // (the device not quite back, a moment's error) is tried again.
      if (isStatus(error, 404) || isStatus(error, 403)) {
        this.continuing = null;
        this.set({ phase: 'ended', reconnect: null, ended: { reason: 'refused', message: isStatus(error, 403) ? 'You don’t have remote access to this device any more.'
          : 'This session can’t be reconnected any more. Reconnect to start a new one.' } });
        return;
      }
      this.waitForDevice(RECONNECT_POLL_MS);
      return;
    }
    if (this.closed) { this.abandon(ticket.id); return; }
    this.connect(ticket.id);
  }

  /** The session is over, however it ended: timers stop and pictures waiting to be drawn are let go. */
  private stopTimers() {
    clearTimeout(this.switchTimer);
    if (this.rateTimer) clearInterval(this.rateTimer);
    if (this.pointerFrame !== null) cancelAnimationFrame(this.pointerFrame);
    this.rateTimer = null; this.pointerFrame = null;
    this.over = true;
    this.dropTiles();
    this.dropClips();
  }
  private over = false;

  private onMessage(text: string) {
    let message: ViewerMessage;
    try { message = JSON.parse(text); } catch { return; }
    if (message.type === 'state') {
      if (message.state === 'connected') {
        const reconnected = !!this.continuing;
        this.letIn = true;
        this.continuing = null;
        this.set({ phase: 'connected', connectedAt: Date.now(), reconnect: null, ...(reconnected ? { notice: 'Reconnected.' } : {}) });
      } else if (message.state === 'dropped') {
        // The device's side closed: the relay is finding out whether the device went offline.
        const since = Date.now();
        this.set({ phase: 'reconnecting', reconnect: { since, until: since + RECONNECT_SECONDS * 1000, restart: !!this.state.restart, checking: true } });
      } else this.set({ phase: this.continuing ? 'reconnecting' : 'connecting' });
    }
    else if (message.type === 'desktop') this.set({ desktopBackend: message.backend, notice: message.unattended
      ? 'GNOME shows the person at the computer that their screen is shared.'
      : 'The person at the computer selects a monitor and allows sharing in GNOME.' });
    else if (message.type === 'notice') this.set({ notice: message.message });
    else if (message.type === 'display-refused') {
      // Not passed on: the device still shows the display it did.
      if (this.switchStage === 'confirm') this.endSwitch(true);
      this.switchNotice = message.message;
      this.set({ notice: message.message });
    }
    else if (message.type === 'clipboard') {
      const waiter = this.clips.get(message.id);
      if (!waiter) return;
      this.clips.delete(message.id);
      clearTimeout(waiter.timer);
      if (message.ok) waiter.resolve({ bytes: message.bytes, text: message.text }); else waiter.reject(new Error(message.error));
    }
    else if (message.type === 'consent') {
      this.set(message.state === 'waiting' ? { consent: { ...message, state: 'waiting', since: Date.now() } }
        : { consent: { state: message.state }, notice: CONSENT_NOTICE[message.state] ?? null });
    }
    else if (message.type === 'ended') {
      const sessionId = this.state.sessionId;
      // Gone offline (or WITTCO RMM restarted) after being let in: reconnect when the device is back. A reconnect that
      // didn't get in tries again, unless it was turned away for good, or the person at the computer ended it (WRMM-176).
      const dropped = this.letIn && RECONNECTABLE[this.options.mode].has(message.reason),
        retry = !!this.continuing && !this.letIn && !FINAL.has(message.reason) && !message.byPerson;
      if (sessionId && !this.options.customer && !this.closed && (dropped || retry)) {
        const socket = this.socket;
        this.socket = null;
        socket?.close();
        this.stopTimers();
        if (retry) this.waitForDevice(RECONNECT_POLL_MS); else this.reconnectAfter(sessionId);
        return;
      }
      this.stopTimers();
      this.continuing = null;
      this.set({ phase: 'ended', reconnect: null, ended: { reason: message.reason, message: message.message } });
    }
  }

  private onScreen(event: ScreenEvent) {
    switch (event.type) {
      case 'size': {
        // A malfunctioning device could ask for a canvas the browser can't hold: anything past three 4K screens side by
        // side (and a little more) ends the session instead.
        if (event.width > MAX_SCREEN_SIDE || event.height > MAX_SCREEN_SIDE || event.width * event.height > MAX_SCREEN_PIXELS) {
          this.fail(`The device reported a ${event.width}×${event.height} screen, too large to show, so the session was ended.`);
          return;
        }
        // A refresh can repeat dimensions. Do not clear the canvas, cancel image
        // decoding or echo settings/unpause for a screen whose size did not change.
        if (this.state.screen?.width === event.width && this.state.screen.height === event.height) break;
        // Input maps onto the new screen only once its picture is drawn (it may be another viewer's display switch).
        this.leavePicture();
        if (!this.switchStage) this.holdForPicture(false);
        const { canvas } = this.options;
        canvas.width = event.width; canvas.height = event.height;
        const context = canvas.getContext('2d');
        if (context) { context.fillStyle = '#0a1620'; context.fillRect(0, 0, event.width, event.height); }
        this.dropTiles();
        this.set({ screen: { width: event.width, height: event.height }, notice: null });
        this.applyQuality();
        this.send(pauseFrame(false));
        this.send(inputLockFrame(2));
        this.send(displaysFrame());
        break;
      }
      case 'tile': {
        // Not yet known to be the display shown (it may still be the one being left): not drawn over the cleared screen.
        if (this.switchStage === 'confirm' || this.switchStage === 'resync') break;
        // The picture must fit the screen the device announced, with its edge tiles' padding; a tile declaring more
        // would be decoded at that size. One that can't be read, or comes before the screen's size, is dropped (it
        // couldn't be drawn anyway).
        const size = jpegSize(event.jpeg), screen = this.state.screen;
        if (!size || !screen) break;
        if (event.x + size.width > padded(screen.width) || event.y + size.height > padded(screen.height)) {
          this.fail('The device sent a screen update that doesn’t fit its screen, so the session was ended.');
          return;
        }
        // Fallen far behind (a slow browser, or a device flooding it): drop what is waiting and have the whole screen
        // sent again, rather than hold an unbounded backlog of pictures.
        if (this.queued + event.jpeg.length > MAX_QUEUED_TILE_BYTES) {
          this.dropTiles();
          if (Date.now() - this.lastRefresh > 1000) { this.lastRefresh = Date.now(); this.refresh(); }
        }
        const blob = new Blob([event.jpeg as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' });
        this.tiles.push({ blob, x: event.x, y: event.y, ready: null });
        this.queued += blob.size;
        this.decodeAhead();
        void this.drawTiles();
        break;
      }
      case 'displays': {
        this.shownDisplay = event.selected;
        const expected = this.switchTarget ?? this.state.display;
        if (this.switchStage === 'confirm') {
          if (event.selected === this.switchTarget) this.switchStage = 'picture';
        } else if (this.switchStage === 'resync') {
          this.switchStage = 'picture'; // The device says what it shows: that is the display now, once it's drawn.
        } else if (expected !== null && event.selected !== expected) {
          // Another viewer of this device chose another display: its old picture goes here too, and input waits.
          this.leavePicture();
          this.switchTarget = null;
          this.holdForPicture(true);
        }
        // While this viewer's own switch is under way, the toolbar keeps showing the display asked for.
        this.set({ displays: event.displays, ...(this.switchTarget !== null ? {} : { display: event.selected }) });
        break;
      }
      case 'monitors': this.set({ monitors: event.monitors }); break;
      case 'input-lock': this.set({ inputLocked: event.locked }); break;
      case 'cursor': this.set({ cursor: event.cursor }); break;
      case 'alert': this.set({ notice: event.message }); break;
      default: break;
    }
  }

  /** Tiles are decoded a few at a time, in the order they came. */
  private decodeAhead() {
    for (const tile of this.tiles) {
      if (this.decoding >= DECODE_AHEAD) break;
      if (tile.ready) continue;
      this.decoding += 1;
      tile.ready = createImageBitmap(tile.blob).catch(() => null).finally(() => { this.decoding -= 1; });
    }
  }

  private dropTiles() {
    for (const tile of this.tiles) void tile.ready?.then(bitmap => bitmap?.close());
    this.tiles = [];
    this.queued = 0;
  }

  private async drawTiles() {
    if (this.drawing) return;
    this.drawing = true;
    const context = this.options.canvas.getContext('2d');
    try {
      while (this.tiles.length && !this.over) {
        const tile = this.tiles[0]!;
        this.decodeAhead();
        // Every decode slot is busy (some with tiles already dropped): wait for one to finish.
        if (!tile.ready) { await new Promise(resolve => setTimeout(resolve, 10)); continue; }
        const bitmap = await tile.ready;
        if (this.tiles[0] !== tile) { bitmap?.close(); continue; } // Dropped meanwhile (the screen changed size, or a backlog).
        this.tiles.shift();
        this.queued -= tile.blob.size;
        if (bitmap) {
          context?.drawImage(bitmap, tile.x, tile.y); bitmap.close();
          if (this.switchStage === 'picture') this.endSwitch(false); // The new display shows: input maps onto what is seen.
        }
      }
    } finally { this.drawing = false; }
  }

  setQuality(quality: Quality) { this.quality = quality; this.applyQuality(); }
  private applyQuality() { const q = QUALITY[this.quality]; this.send(settingsFrame(q.quality, q.scaling, q.frameMs)); }
  refresh() { this.send(refreshFrame()); }
  /**
   * Shows another of the device's displays (or all of them). MeshAgent then sends that display's size, its display list
   * naming it as shown, and its picture. Until that picture is drawn the pointer is held back, so nothing is mapped onto
   * the display being left; and no other switch starts, so no answer can be taken for the wrong one.
   */
  setDisplay(display: number) {
    // One switch at a time: a second would leave the screen and the pointer unsure which display they belong to.
    if (this.switchStage || display === this.state.display || !this.state.displays.includes(display)) return;
    this.leavePicture();
    this.send(setDisplayFrame(display));
    this.switchStage = 'confirm';
    this.switchTarget = display;
    this.set({ display, switching: true });
    clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => this.endSwitch(true), SWITCH_MS);
  }

  /**
   * What was shown is no longer what input lands on: let go of held buttons (not dragged across), drop a move waiting for
   * the next frame and part of a wheel notch, and clear the picture so nothing on it can be clicked (the new screen may
   * be the same size).
   */
  private leavePicture() {
    this.releaseButtons();
    if (this.pointerFrame !== null) { cancelAnimationFrame(this.pointerFrame); this.pointerFrame = null; }
    this.pointer = null;
    this.wheel = 0;
    this.dropTiles();
    const { canvas } = this.options, context = canvas.getContext('2d');
    if (context) { context.fillStyle = '#0a1620'; context.fillRect(0, 0, canvas.width, canvas.height); }
  }

  /** Holds input until the next picture is drawn, or SWITCH_MS at the latest; `display` when it's a display switch. */
  private holdForPicture(display: boolean) {
    this.switchStage = 'picture';
    if (display) this.set({ switching: true });
    clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => this.endSwitch(true), SWITCH_MS);
  }

  /**
   * The screen shown is the one input lands on. Or (late) that didn't happen in time, or the switch wasn't passed on:
   * then input stays held while the viewer asks the device again which display it shows and for its whole screen, and
   * goes by what it says (asking again every SWITCH_MS, never letting input through blind).
   */
  private endSwitch(late: boolean) {
    if (!this.switchStage) return;
    clearTimeout(this.switchTimer);
    if (late) {
      const again = this.switchStage === 'resync';
      this.switchStage = 'resync';
      this.switchTarget = null;
      this.dropTiles();
      if (again) this.switchNotice = WAITING_NOTICE;
      this.set({ ...(this.shownDisplay !== null ? { display: this.shownDisplay } : {}), ...(again ? { notice: WAITING_NOTICE } : {}) });
      this.send(displaysFrame());
      this.refresh();
      this.switchTimer = setTimeout(() => this.endSwitch(true), SWITCH_MS);
      return;
    }
    this.switchStage = null;
    this.switchTarget = null;
    const stale = this.switchNotice !== null && this.state.notice === this.switchNotice;
    this.switchNotice = null;
    this.set({ switching: false, ...(stale ? { notice: null } : {}) });
  }
  ctrlAltDel() { if (this.control && this.state.desktopBackend !== 'wayland') this.send(ctrlAltDelFrame()); }
  /** Blocks or unblocks the device's own keyboard and mouse while the technician works. */
  lockInput(locked: boolean) { if (this.control && this.state.desktopBackend !== 'wayland') { this.send(inputLockFrame(locked ? 1 : 0)); this.send(inputLockFrame(2)); } }
  special(id: string) {
    const combo = SPECIAL_KEYS.find(k => k.id === id);
    if (!combo || !this.control) return;
    for (const [code, extended, down] of combo.steps) this.send(keyFrame(down ? 1 : 2, code, extended));
  }

  /** Puts text on the device's clipboard (WRMM-63), for pasting there. Resolves with its size once it's there. */
  async sendClipboard(text: string) { return (await this.clipboard({ direction: 'to_device', text })).bytes; }
  /** The text on the device's clipboard (WRMM-63). */
  async fetchClipboard() { return (await this.clipboard({ direction: 'from_device' })).text ?? ''; }
  private clipboard(command: { direction: 'to_device'; text: string } | { direction: 'from_device' }) {
    return new Promise<{ bytes: number; text?: string }>((resolve, reject) => {
      if (!this.control || this.state.phase !== 'connected' || this.socket?.readyState !== WebSocket.OPEN) { reject(new Error('The session isn’t connected.')); return; }
      const id = `c${++this.clipCount}`;
      const timer = setTimeout(() => { this.clips.delete(id); reject(new Error('The device didn’t answer. Try again in a moment.')); }, CLIPBOARD_WAIT_MS);
      this.clips.set(id, { resolve, reject, timer });
      this.socket.send(JSON.stringify({ type: 'clipboard', id, ...command } satisfies ClipboardCommand));
    });
  }
  /** Copy and paste requests still waiting when the session ends or drops are answered: they won't be any more. */
  private dropClips() {
    for (const waiter of this.clips.values()) { clearTimeout(waiter.timer); waiter.reject(new Error('The session ended.')); }
    this.clips.clear();
  }

  /** Types text on the device as keystrokes, for when its clipboard isn't shared: line breaks become Enter. */
  typeText(text: string) {
    if (!this.control) return;
    const frames: Uint8Array[] = [];
    for (const char of text.replace(/\r\n?/g, '\n')) {
      if (char === '\n') frames.push(keyFrame(1, 13), keyFrame(2, 13));
      else if (char === '\t') frames.push(keyFrame(1, 9), keyFrame(2, 9));
      else for (let i = 0; i < char.length; i++) { const unit = char.charCodeAt(i); frames.push(unicodeFrame(1, unit), unicodeFrame(2, unit)); }
    }
    for (let i = 0; i < frames.length; i += 200) {
      const batch = frames.slice(i, i + 200), bytes = new Uint8Array(batch.reduce((n, f) => n + f.length, 0));
      batch.reduce((at, f) => { bytes.set(f, at); return at + f.length; }, 0);
      this.send(bytes);
    }
  }

  /** A position on the canvas as it's displayed, in the device's screen coordinates. */
  private position(event: { clientX: number; clientY: number }) {
    const screen = this.state.screen;
    if (!screen || this.switchStage) return null; // Until the picture shown is the screen input lands on.
    const box = this.options.canvas.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    const x = Math.round((event.clientX - box.left) * screen.width / box.width), y = Math.round((event.clientY - box.top) * screen.height / box.height);
    return { x: Math.max(0, Math.min(screen.width - 1, x)), y: Math.max(0, Math.min(screen.height - 1, y)) };
  }

  pointerMove(event: { clientX: number; clientY: number }) {
    if (!this.control) return;
    this.pointer = this.position(event);
    // At most one move per animation frame, always the latest position.
    if (this.pointerFrame === null) this.pointerFrame = requestAnimationFrame(() => {
      this.pointerFrame = null;
      if (this.pointer) this.send(mouseFrame(this.pointer.x, this.pointer.y, MouseButton.NONE, false));
    });
  }
  pointerButton(event: { clientX: number; clientY: number; button: number }, down: boolean) {
    if (!this.control) return;
    const at = this.position(event);
    const button = event.button === 0 ? MouseButton.LEFT : event.button === 1 ? MouseButton.MIDDLE : event.button === 2 ? MouseButton.RIGHT : null;
    if (!at || button === null) return;
    if (down) this.buttons.set(button, at); else this.buttons.delete(button);
    this.send(mouseFrame(at.x, at.y, button, down));
  }
  /**
   * Lets go of mouse buttons still down when the browser cancels the pointer (a window switch, say) or the viewer
   * loses focus, so the device isn't left mid-drag.
   */
  releaseButtons() {
    for (const [button, at] of this.buttons) { const { x, y } = this.pointer ?? at; this.send(mouseFrame(x, y, button, false)); }
    this.buttons.clear();
  }
  /** Wheel movement, gathered into the 120-unit notches Windows programs expect. */
  wheelMove(event: { clientX: number; clientY: number; deltaY: number; deltaMode: number }) {
    if (!this.control) return;
    const at = this.position(event);
    if (!at) return;
    this.wheel -= event.deltaY * (event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 800 : 1.2);
    const notches = Math.trunc(this.wheel / 120);
    if (!notches) return;
    this.wheel -= notches * 120;
    this.send(wheelFrame(at.x, at.y, notches * 120));
  }

  /**
   * The technician's keyboard: on a Mac, ⌘ can reach the device as Ctrl, so ⌘C, ⌘V and ⌘Z copy, paste and undo there as
   * they do on the Mac.
   */
  setKeyboard(keyboard: { mac: boolean; commandAsCtrl: boolean }) { this.keyboard = keyboard; }

  key(event: Pick<KeyboardEvent, 'code' | 'key' | 'keyCode' | 'ctrlKey' | 'altKey' | 'metaKey' | 'repeat'>, down: boolean) {
    if (!this.control) return;
    if (down) this.released.delete(event.code);
    else if (this.released.delete(event.code)) return; // Let go already, when it was pressed.
    // From when ⌘ goes down, macOS won't report letting go of keys already held either: let go of them now.
    if (down && this.keyboard.mac && isCommand(event.code)) this.releaseBeforeCommand();
    if (down && typesCharacter(event)) {
      const units = [...event.key].flatMap(c => Array.from({ length: c.length }, (_, i) => c.charCodeAt(i)));
      // A held key repeating as a different character (Shift pressed meanwhile): let go of what it sent before.
      const before = this.typed.get(event.code);
      if (before && before.join() !== units.join()) for (const unit of before) this.send(unicodeFrame(2, unit));
      for (const unit of units) this.send(unicodeFrame(1, unit));
      this.typed.set(event.code, units);
      return;
    }
    if (!down && this.typed.has(event.code)) {
      for (const unit of this.typed.get(event.code)!) this.send(unicodeFrame(2, unit));
      this.typed.delete(event.code);
      return;
    }
    let vk = virtualKey(event);
    if (vk && this.keyboard.mac && this.keyboard.commandAsCtrl && isCommand(event.code)) vk = { code: 17, extended: event.code.endsWith('Right') };
    // A ⌘ shortcut is the letter or digit the Mac's layout gives, not the key's US position (⌘Z on a German layout is
    // the key where US has Y).
    else if (vk && this.keyboard.mac && event.metaKey && /^[a-z0-9]$/i.test(event.key) && !event.code.startsWith('Numpad')) {
      vk = { code: event.key.toUpperCase().charCodeAt(0), extended: false };
    }
    if (!down) vk = this.held.get(event.code) ?? vk;
    if (!vk) return;
    if (down && this.keyboard.mac && event.metaKey && !isModifier(event.code)) {
      // macOS doesn't report letting go of a key pressed while ⌘ is held, so it would stay down on the device (and repeat).
      this.send(keyFrame(1, vk.code, vk.extended));
      this.send(keyFrame(2, vk.code, vk.extended));
      for (const unit of this.typed.get(event.code) ?? []) this.send(unicodeFrame(2, unit)); // Held before ⌘ went down.
      this.typed.delete(event.code);
      this.held.delete(event.code);
      this.released.add(event.code);
      return;
    }
    // Two physical keys sent as one device key (Control and ⌘ as Ctrl): it goes down with the first and up with the last.
    const shared = (key: VirtualKey) => [...this.held].some(([code, other]) => code !== event.code && other.code === key.code && other.extended === key.extended);
    if (down) {
      const repeat = this.held.has(event.code);
      this.held.set(event.code, vk);
      if (!repeat && shared(vk)) return;
    } else {
      this.held.delete(event.code);
      if (shared(vk)) return;
    }
    this.send(keyFrame(down ? 1 : 2, vk.code, vk.extended));
  }

  private releaseBeforeCommand() {
    for (const [code, units] of this.typed) { for (const unit of units) this.send(unicodeFrame(2, unit)); this.typed.delete(code); this.released.add(code); }
    for (const [code, vk] of this.held) {
      if (isModifier(code)) continue;
      this.send(keyFrame(2, vk.code, vk.extended));
      this.held.delete(code);
      this.released.add(code);
    }
  }

  /** Lets go of every key and mouse button still down, when the viewer loses focus, so nothing stays stuck on the device. */
  releaseKeys() {
    for (const [code, units] of this.typed) { for (const unit of units) this.send(unicodeFrame(2, unit)); this.typed.delete(code); }
    for (const [code, vk] of this.held) { this.send(keyFrame(2, vk.code, vk.extended)); this.held.delete(code); }
    this.released.clear();
    this.releaseButtons();
  }
}

/**
 * A display's name in the viewer, with its size and where it sits beside the main monitor (at 0,0 on Windows) when the
 * device has said where its monitors are.
 */
export function displayName(display: number, monitors: Monitor[] = []) {
  if (display === ALL_DISPLAYS) return 'All displays';
  const monitor = monitors.find(m => m.id === display);
  if (!monitor) return `Display ${display}`;
  return `Display ${display} · ${monitor.width}×${monitor.height}${monitors.length > 1 ? ` · ${placement(monitor, monitors)}` : ''}`;
}

function placement(monitor: Monitor, monitors: Monitor[]) {
  const main = monitors.find(m => m.x === 0 && m.y === 0);
  if (!main) return `at ${monitor.x}, ${monitor.y}`;
  if (main === monitor) return 'main';
  // Compared by centres: the larger difference says which side it's on.
  const dx = (monitor.x + monitor.width / 2) - (main.width / 2), dy = (monitor.y + monitor.height / 2) - (main.height / 2);
  return Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? 'left of main' : 'right of main') : (dy < 0 ? 'above main' : 'below main');
}
